import {
  Component,
  OnInit,
} from "@angular/core";

import {
  finalize,
} from "rxjs/operators";

import {
  ToastrService,
} from "ngx-toastr";

import {
  UserAssignedMenuItem,
  UserMenuModuleEntity,
  UserMenuModuleName,
  UserMenuUserItem,
} from "./usermenu-config-interface";

import {
  UsermenuConfigService,
} from "./usermenu-config.service";


interface UserMenuAffiliationRole {

  roleId: number;

  roleName: string;

  roleCode: string;

  groupName: string;

  moduleName: string;

  moduleId: number;

  affiliationType: string;

  isActive: number;

  isDeleted: number;
}


@Component({
  selector:
    "app-usermenu-config",

  templateUrl:
    "./usermenu-config.component.html",

  styleUrls: [
    "./usermenu-config.component.css",
  ],
})
export class UsermenuConfigComponent
  implements OnInit {


  // ============================================================
  // MODULE
  // ============================================================

  moduleOptions:
    UserMenuModuleName[] = [
      "School",
      "Hospital",
      "Society",
      "Common",
    ];


  selectedModuleName:
    UserMenuModuleName | "" = "";


  // ============================================================
  // ONBOARDED ENTITY
  // ============================================================

  moduleEntities:
    UserMenuModuleEntity[] = [];


  selectedModuleId:
    number = 0;


  // ============================================================
  // AFFILIATION / USER
  // ============================================================

  affiliationRoles:
    UserMenuAffiliationRole[] = [];


  users:
    UserMenuUserItem[] = [];


  selectedAffiliationType:
    string = "";


  selectedUserId:
    number = 0;


  // ============================================================
  // ASSIGNED MENUS
  // ============================================================

  assignedMenus:
    UserAssignedMenuItem[] = [];


  searchText:
    string = "";


  // ============================================================
  // LOADING
  // ============================================================

  loadingEntities:
    boolean = false;


  loadingAffiliations:
    boolean = false;


  loadingUsers:
    boolean = false;


  loadingMenus:
    boolean = false;


  updatingSubRoleIds:
    Set<number> =
      new Set<number>();


  constructor(
    private service:
      UsermenuConfigService,

    private toastr:
      ToastrService,
  ) {}


  ngOnInit(): void {}


  // ============================================================
  // LABELS
  // ============================================================

  get moduleEntityLabel():
    string {

    if (
      this.selectedModuleName ===
      "School"
    ) {

      return "Onboarded School";
    }


    if (
      this.selectedModuleName ===
      "Hospital"
    ) {

      return "Onboarded Hospital";
    }


    if (
      this.selectedModuleName ===
      "Society"
    ) {

      return "Onboarded Society";
    }


    return "Onboarded Module";
  }


  get moduleEntityPlaceholder():
    string {

    if (
      this.loadingEntities
    ) {

      return "Loading...";
    }


    if (
      this.selectedModuleName ===
      "School"
    ) {

      return "Select Onboarded School";
    }


    if (
      this.selectedModuleName ===
      "Hospital"
    ) {

      return "Select Onboarded Hospital";
    }


    if (
      this.selectedModuleName ===
      "Society"
    ) {

      return "Select Onboarded Society";
    }


    return "Not Required";
  }


  get requiresModuleEntity():
    boolean {

    return (
      !!this.selectedModuleName
      &&
      this.selectedModuleName !==
        "Common"
    );
  }


  // ============================================================
  // AFFILIATION TYPES
  // ============================================================

  get affiliationTypes():
    string[] {

    const values =
      new Set<string>();


    this.affiliationRoles.forEach(
      (role) => {

        const affiliation =
          String(
            role.affiliationType ||
            "",
          ).trim();


        if (
          affiliation
        ) {

          values.add(
            affiliation,
          );
        }
      },
    );


    return Array.from(
      values,
    ).sort(
      (a, b) =>
        a.localeCompare(
          b,
        ),
    );
  }


  // ============================================================
  // FILTERED USERS
  // ============================================================

  get filteredUsers():
    UserMenuUserItem[] {

    return this.users
      .filter(
        (user) => {

          if (
            !this.selectedAffiliationType
          ) {

            return true;
          }


          return (
            String(
              user.affiliationType ||
              "",
            )
              .trim()
              .toLowerCase()
            ===
            String(
              this.selectedAffiliationType,
            )
              .trim()
              .toLowerCase()
          );
        },
      )
      .sort(
        (a, b) =>
          String(
            a.displayName ||
            "",
          ).localeCompare(
            String(
              b.displayName ||
              "",
            ),
          ),
      );
  }


  // ============================================================
  // SELECTED USER
  // ============================================================

  get selectedUser():
    UserMenuUserItem | null {

    return (
      this.users.find(
        (user) =>
          Number(
            user.userId,
          )
          ===
          Number(
            this.selectedUserId,
          ),
      )
      ||
      null
    );
  }


  // ============================================================
  // FILTERED ASSIGNED MENUS
  // ============================================================

  get filteredAssignedMenus():
    UserAssignedMenuItem[] {

    const search =
      String(
        this.searchText ||
        "",
      )
        .trim()
        .toLowerCase();


    const moduleName =
      this.normalizeModuleName(
        this.selectedModuleName,
      );


    return this.assignedMenus
      .filter(
        (menu) => {

          const menuModule =
            this.normalizeModuleName(
              menu.moduleName,
            );


          /*
           * Keep blank/Common rows visible because older SubRoles
           * records can have blank/Global ModuleName.
           */
          const moduleMatches =
            !moduleName
            ||
            !menuModule
            ||
            menuModule ===
              moduleName
            ||
            menuModule ===
              "common";


          if (
            !moduleMatches
          ) {

            return false;
          }


          if (
            !search
          ) {

            return true;
          }


          return (
            String(
              menu.menuName ||
              "",
            )
              .toLowerCase()
              .includes(search)

            ||

            String(
              menu.menuCode ||
              "",
            )
              .toLowerCase()
              .includes(search)

            ||

            String(
              menu.templatePath ||
              "",
            )
              .toLowerCase()
              .includes(search)

            ||

            String(
              menu.moduleName ||
              "",
            )
              .toLowerCase()
              .includes(search)
          );
        },
      )
      .sort(
        (a, b) => {

          const orderDifference =
            Number(
              a.displayOrder ||
              0,
            )
            -
            Number(
              b.displayOrder ||
              0,
            );


          if (
            orderDifference !==
            0
          ) {

            return orderDifference;
          }


          return String(
            a.menuName ||
            "",
          ).localeCompare(
            String(
              b.menuName ||
              "",
            ),
          );
        },
      );
  }


  // ============================================================
  // MODULE CHANGE
  // ============================================================

  onModuleChange(): void {

    this.moduleEntities = [];

    this.selectedModuleId = 0;

    this.affiliationRoles = [];

    this.users = [];

    this.selectedAffiliationType = "";

    this.selectedUserId = 0;

    this.assignedMenus = [];

    this.searchText = "";


    if (
      !this.selectedModuleName
    ) {

      return;
    }


    if (
      this.selectedModuleName ===
      "Common"
    ) {

      this.loadAffiliationRoles();

      return;
    }


    this.loadModuleEntities();
  }


  // ============================================================
  // LOAD ONBOARDED ENTITY
  // ============================================================

  loadModuleEntities(): void {

    if (
      !this.selectedModuleName
      ||
      this.selectedModuleName ===
        "Common"
      ||
      this.loadingEntities
    ) {

      return;
    }


    this.loadingEntities =
      true;


    this.service
      .readModuleEntities(
        this.selectedModuleName,
      )
      .pipe(
        finalize(() => {

          this.loadingEntities =
            false;
        }),
      )
      .subscribe({

        next: (response) => {

          const records =
            this.extractArray(
              response,
            );


          this.moduleEntities =
            records
              .filter(
                (item: any) =>
                  this.isActiveRecord(
                    item,
                  ),
              )
              .map(
                (item: any) =>
                  this.mapModuleEntity(
                    item,
                    this.selectedModuleName as
                      UserMenuModuleName,
                  ),
              )
              .filter(
                (item) =>
                  item.id > 0
                  &&
                  !!item.name,
              )
              .sort(
                (a, b) =>
                  a.name.localeCompare(
                    b.name,
                  ),
              );

        },


        error: (error) => {

          console.error(
            "User Menu Config - Module Entity Error:",
            error,
          );


          this.moduleEntities = [];


          this.showError(
            error,
            `Unable to load ${this.moduleEntityLabel}.`,
          );
        },

      });
  }


  // ============================================================
  // MODULE ENTITY CHANGE
  // ============================================================

  onModuleEntityChange(): void {

    this.affiliationRoles = [];

    this.users = [];

    this.selectedAffiliationType = "";

    this.selectedUserId = 0;

    this.assignedMenus = [];

    this.searchText = "";


    if (
      this.requiresModuleEntity
      &&
      this.selectedModuleId <= 0
    ) {

      return;
    }


    this.loadAffiliationRoles();
  }


  // ============================================================
  // LOAD USER AFFILIATIONS FROM ROLES
  //
  // MINIMUM FIX:
  // After onboarded module selection, do NOT call
  // UserAffiliationMapping_ReadUserBy... immediately.
  //
  // First call Roles_ReadAllPaginated and populate the
  // User Affiliation dropdown from Roles.AffiliationType.
  // ============================================================

  loadAffiliationRoles(): void {

    if (
      !this.selectedModuleName
      ||
      this.loadingAffiliations
    ) {

      return;
    }


    if (
      this.requiresModuleEntity
      &&
      this.selectedModuleId <= 0
    ) {

      return;
    }


    this.loadingAffiliations =
      true;


    this.service
      .readAffiliationRoles()
      .pipe(
        finalize(() => {

          this.loadingAffiliations =
            false;
        }),
      )
      .subscribe({

        next: (response) => {

          const selectedModule =
            this.normalizeModuleName(
              this.selectedModuleName,
            );


          this.affiliationRoles =
            this.extractArray(
              response,
            )
              .map(
                (item: any) =>
                  this.mapAffiliationRole(
                    item,
                  ),
              )
              .filter(
                (role) =>
                  role.isActive === 1
                  &&
                  role.isDeleted === 0
                  &&
                  !!role.affiliationType,
              )
              .filter(
                (role) => {

                  /*
                   * IMPORTANT FIX:
                   * Existing Roles data is mixed:
                   * - many old School/Hospital/Society roles have
                   *   ModuleName = NULL and ModuleId = 0
                   * - but GroupName correctly contains School / Hospital / Society
                   * - newer records can contain ModuleName as well
                   *
                   * Therefore User Affiliation must be filtered by the
                   * role's module/group, NOT by the selected onboarded
                   * SchoolId/HospitalId/SocietyId.
                   */
                  const roleModule =
                    this.normalizeModuleName(
                      role.moduleName,
                    );


                  const roleGroup =
                    this.normalizeModuleName(
                      role.groupName,
                    );


                  return (
                    roleModule ===
                      selectedModule
                    ||
                    roleGroup ===
                      selectedModule
                  );
                },
              );


          if (
            this.affiliationTypes.length === 0
          ) {

            this.toastr.info(
              "No user affiliations found for the selected module.",
            );
          }

        },


        error: (error) => {

          console.error(
            "User Menu Config - Roles ReadAllPaginated Error:",
            error,
          );


          this.affiliationRoles = [];


          this.showError(
            error,
            "Unable to load user affiliations.",
          );
        },

      });
  }


  // ============================================================
  // LOAD USERS
  // ============================================================

  loadUsers(): void {

    if (
      !this.selectedModuleName
      ||
      !this.selectedAffiliationType
      ||
      this.loadingUsers
    ) {

      return;
    }


    if (
      this.requiresModuleEntity
      &&
      this.selectedModuleId <= 0
    ) {

      return;
    }


    this.loadingUsers =
      true;


    this.service
      .readUsersByModuleEntity(
        this.selectedModuleName,
        Number(
          this.selectedModuleId ||
          0,
        ),
      )
      .pipe(
        finalize(() => {

          this.loadingUsers =
            false;
        }),
      )
      .subscribe({

        next: (response) => {

          const records =
            this.extractArray(
              response,
            );


          const selectedAffiliation =
            String(
              this.selectedAffiliationType ||
              "",
            )
              .trim()
              .toLowerCase();


          const userMap =
            new Map<
              number,
              UserMenuUserItem
            >();


          records
            .filter(
              (item: any) =>
                this.isActiveRecord(
                  item,
                ),
            )
            .map(
              (item: any) =>
                this.mapUser(
                  item,
                ),
            )
            .filter(
              (user) =>
                user.userId > 0,
            )
            .filter(
              (user) =>
                String(
                  user.affiliationType ||
                  "",
                )
                  .trim()
                  .toLowerCase()
                ===
                selectedAffiliation,
            )
            .forEach(
              (user) => {

                if (
                  !userMap.has(
                    user.userId,
                  )
                ) {

                  userMap.set(
                    user.userId,
                    user,
                  );
                }
              },
            );


          this.users =
            Array.from(
              userMap.values(),
            )
              .sort(
                (a, b) =>
                  a.displayName.localeCompare(
                    b.displayName,
                  ),
              );


          if (
            this.users.length === 0
          ) {

            this.toastr.info(
              "No active users found for the selected affiliation.",
            );
          }

        },


        error: (error) => {

          console.error(
            "User Menu Config - Users Error:",
            error,
          );


          this.users = [];


          this.showError(
            error,
            "Unable to load users.",
          );
        },

      });
  }


  // ============================================================
  // AFFILIATION CHANGE
  // ============================================================

  onAffiliationChange(): void {

    this.users = [];

    this.selectedUserId = 0;

    this.assignedMenus = [];

    this.searchText = "";


    if (
      !this.selectedAffiliationType
    ) {

      return;
    }


    this.loadUsers();
  }


  // ============================================================
  // USER CHANGE
  // ============================================================

  onUserChange(): void {

    this.assignedMenus = [];

    this.searchText = "";


    if (
      this.selectedUserId <= 0
    ) {

      return;
    }


    this.loadAssignedMenus();
  }


  // ============================================================
  // READ USER ASSIGNED MENUS
  // ============================================================

  loadAssignedMenus(): void {

    if (
      this.selectedUserId <= 0
      ||
      this.loadingMenus
    ) {

      return;
    }


    this.loadingMenus =
      true;


    this.service
      .readAssignedMenusByUserId({

        userId:
          Number(
            this.selectedUserId,
          ),

        /*
         * IMPORTANT:
         *
         * User Menu Config must receive BOTH states:
         *
         * SHOWN:
         * IsActive  = 1
         * IsDeleted = 0
         *
         * HIDDEN:
         * IsActive  = 0
         * IsDeleted = 0
         *
         * includeInactive = 1 keeps hidden menus in
         * this configuration grid after refresh/login.
         */
        includeInactive: 1,

        /*
         * IMPORTANT FIX:
         * Read the selected user's menu override only for the
         * selected onboarded module/entity.
         *
         * Example:
         * School + Dev Public School -> ModuleId = selected SchoolId.
         */
        moduleName:
          String(
            this.selectedModuleName ||
            "",
          ),

        moduleId:
          Number(
            this.selectedModuleId ||
            0,
          ),

      })
      .pipe(
        finalize(() => {

          this.loadingMenus =
            false;

        }),
      )
      .subscribe({

        next: (response) => {

          this.assignedMenus =
            this.extractArray(
              response,
            )
              .map(
                (item: any) =>
                  this.mapAssignedMenu(
                    item,
                  ),
              )
              .filter(
                (item) =>
                  item.subRoleId > 0
                  &&
                  item.menuId > 0
                  &&
                  item.isDeleted === 0,
              );


          if (
            this.assignedMenus.length === 0
          ) {

            this.toastr.info(
              "No assigned menus found for this user.",
            );
          }

        },


        error: (error) => {

          console.error(
            "User Menu Config - ReadByUserId Error:",
            error,
          );


          this.assignedMenus = [];


          this.showError(
            error,
            "Unable to load assigned menus.",
          );
        },

      });
  }


  // ============================================================
  // TOGGLE MENU
  // EXISTING /M/SR/Update API
  //
  // USER MENU CONFIG STATE:
  //
  // SHOWN:
  // IsActive  = 1
  // IsDeleted = 0
  //
  // HIDDEN:
  // IsActive  = 0
  // IsDeleted = 0
  //
  // IMPORTANT:
  // IsDeleted must remain 0 for both Show and Hide.
  // ============================================================

  toggleMenu(
    row:
      UserAssignedMenuItem,
    event:
      Event,
  ): void {

    const checkbox =
      event.target as
        HTMLInputElement;


    const nextStatus =
      checkbox.checked
        ? 1
        : 0;


    /*
     * USER MENU CONFIG:
     *
     * Show:
     * IsActive  = 1
     * IsDeleted = 0
     *
     * Hide:
     * IsActive  = 0
     * IsDeleted = 0
     *
     * We are NOT logically deleting the menu.
     */
    const nextDeletedStatus =
      0;


    const previousStatus =
      Number(
        row.isActive ??
        1,
      );


    const previousDeletedStatus =
      Number(
        row.isDeleted ??
        0,
      );


    if (
      row.subRoleId <= 0
      ||
      this.isUpdating(
        row.subRoleId,
      )
    ) {

      checkbox.checked =
        previousStatus === 1
        &&
        previousDeletedStatus === 0;

      return;
    }


    this.updatingSubRoleIds.add(
      row.subRoleId,
    );


    /*
     * Update local UI immediately.
     *
     * This ensures:
     *
     * 1 / 0 -> Shown
     * 0 / 0 -> Hidden
     */
    row.isActive =
      nextStatus;


    row.isDeleted =
      nextDeletedStatus;


    this.service
      .updateSubRole({

        subRoleId:
          Number(
            row.subRoleId,
          ),

        isActive:
          nextStatus,

        /*
         * IMPORTANT FIX:
         *
         * IsDeleted stays 0 for BOTH:
         *
         * Enabled -> 1 / 0
         * Hidden  -> 0 / 0
         */
        isDeleted:
          nextDeletedStatus,

        /*
         * IMPORTANT:
         * Apply this toggle only for the selected target user.
         * Backend creates/updates a user-specific SubRoles override
         * instead of changing the shared role-level SubRole row.
         */
        userId:
          Number(
            this.selectedUserId,
          ),

        /*
         * IMPORTANT FIX:
         * Scope this user-specific override to the selected
         * onboarded School / Hospital / Society.
         */
        moduleName:
          String(
            this.selectedModuleName ||
            "",
          ),

        moduleId:
          Number(
            this.selectedModuleId ||
            0,
          ),

        actionUser:
          this.getActionUser(),

      })
      .pipe(
        finalize(() => {

          this.updatingSubRoleIds.delete(
            row.subRoleId,
          );
        }),
      )
      .subscribe({

        next: () => {

          this.toastr.success(
            nextStatus === 1
              ? "Menu enabled successfully."
              : "Menu disabled successfully.",
          );

        },


        error: (error) => {

          /*
           * Restore exact previous DB/UI state
           * if update fails.
           */
          row.isActive =
            previousStatus;


          row.isDeleted =
            previousDeletedStatus;


          checkbox.checked =
            previousStatus === 1
            &&
            previousDeletedStatus === 0;


          this.showError(
            error,
            "Unable to update menu.",
          );
        },

      });
  }


  // ============================================================
  // UPDATING
  // ============================================================

  isUpdating(
    subRoleId:
      number,
  ): boolean {

    return this.updatingSubRoleIds.has(
      Number(
        subRoleId,
      ),
    );
  }


  // ============================================================
  // REFRESH
  // ============================================================

  refresh(): void {

    if (
      this.selectedUserId > 0
    ) {

      this.loadAssignedMenus();

      return;
    }


    if (
      this.selectedAffiliationType
    ) {

      this.loadUsers();

      return;
    }


    if (
      this.selectedModuleName
      &&
      (
        !this.requiresModuleEntity
        ||
        this.selectedModuleId > 0
      )
    ) {

      this.loadAffiliationRoles();

      return;
    }


    if (
      this.selectedModuleName
    ) {

      this.loadModuleEntities();
    }
  }


  // ============================================================
  // MAP MODULE ENTITY
  // ============================================================

  private mapModuleEntity(
    item: any,
    moduleName:
      UserMenuModuleName,
  ): UserMenuModuleEntity {

    if (
      moduleName === "School"
    ) {

      return {

        id:
          Number(
            item?.schoolId ??
            item?.SchoolId ??
            item?.id ??
            item?.Id ??
            0,
          ),

        name:
          String(
            item?.schoolName ??
            item?.SchoolName ??
            item?.name ??
            item?.Name ??
            "",
          ).trim(),

      };
    }


    if (
      moduleName === "Hospital"
    ) {

      return {

        id:
          Number(
            item?.hospitalId ??
            item?.HospitalId ??
            item?.id ??
            item?.Id ??
            0,
          ),

        name:
          String(
            item?.hospitalName ??
            item?.HospitalName ??
            item?.name ??
            item?.Name ??
            "",
          ).trim(),

      };
    }


    return {

      id:
        Number(
          item?.societyId ??
          item?.SocietyId ??
          item?.id ??
          item?.Id ??
          0,
        ),

      name:
        String(
          item?.societyName ??
          item?.SocietyName ??
          item?.name ??
          item?.Name ??
          "",
        ).trim(),

    };
  }


  // ============================================================
  // MAP AFFILIATION ROLE
  // ============================================================

  private mapAffiliationRole(
    item: any,
  ): UserMenuAffiliationRole {

    return {

      roleId:
        Number(
          item?.roleId ??
          item?.RoleId ??
          0,
        ),

      roleName:
        String(
          item?.roleName ??
          item?.RoleName ??
          "",
        ).trim(),

      roleCode:
        String(
          item?.roleCode ??
          item?.RoleCode ??
          "",
        ).trim(),

      groupName:
        String(
          item?.groupName ??
          item?.GroupName ??
          "",
        ).trim(),

      moduleName:
        String(
          item?.moduleName ??
          item?.ModuleName ??
          "",
        ).trim(),

      moduleId:
        Number(
          item?.moduleId ??
          item?.ModuleId ??
          0,
        ),

      affiliationType:
        String(
          item?.affiliationType ??
          item?.AffiliationType ??
          "",
        ).trim(),

      isActive:
        Number(
          item?.isActive ??
          item?.IsActive ??
          1,
        ),

      isDeleted:
        Number(
          item?.isDeleted ??
          item?.IsDeleted ??
          0,
        ),

    };
  }


  // ============================================================
  // MAP USER
  // ============================================================

  private mapUser(
    item: any,
  ): UserMenuUserItem {

    const firstName =
      String(
        item?.firstName ??
        item?.FirstName ??
        "",
      ).trim();


    const middleName =
      String(
        item?.middleName ??
        item?.MiddleName ??
        "",
      ).trim();


    const lastName =
      String(
        item?.lastName ??
        item?.LastName ??
        "",
      ).trim();


    const combinedName =
      [
        firstName,
        middleName,
        lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();


    const userId =
      Number(
        item?.userId ??
        item?.UserId ??
        item?.id ??
        item?.Id ??
        0,
      );


    const displayName =
      combinedName
      ||
      String(
        item?.userName ??
        item?.UserName ??
        item?.name ??
        item?.Name ??
        item?.primaryName ??
        item?.PrimaryName ??
        item?.fullName ??
        item?.FullName ??
        "",
      ).trim()
      ||
      `User ${userId}`;


    return {

      userId,

      displayName,

      mobileNo:
        String(
          item?.mobileNo ??
          item?.MobileNo ??
          item?.mobileNumber ??
          item?.MobileNumber ??
          item?.contactNumber ??
          item?.ContactNumber ??
          "",
        ).trim(),

      email:
        String(
          item?.email ??
          item?.Email ??
          item?.emailId ??
          item?.EmailId ??
          "",
        ).trim(),

      affiliationType:
        String(
          item?.affiliationType ??
          item?.AffiliationType ??
          item?.userAffiliationType ??
          item?.UserAffiliationType ??
          item?.designation ??
          item?.Designation ??
          item?.roleName ??
          item?.RoleName ??
          "",
        ).trim(),

      roleId:
        Number(
          item?.roleId ??
          item?.RoleId ??
          0,
        ),

      roleName:
        String(
          item?.roleName ??
          item?.RoleName ??
          "",
        ).trim(),

      moduleId:
        Number(
          item?.moduleId ??
          item?.ModuleId ??
          this.selectedModuleId ??
          0,
        ),

      moduleName:
        String(
          item?.moduleName ??
          item?.ModuleName ??
          this.selectedModuleName ??
          "",
        ).trim(),

    };
  }


  // ============================================================
  // MAP ASSIGNED MENU
  // ============================================================

  private mapAssignedMenu(
    item: any,
  ): UserAssignedMenuItem {

    return {

      subRoleId:
        Number(
          item?.subRoleId ??
          item?.SubRoleId ??
          0,
        ),

      roleId:
        Number(
          item?.roleId ??
          item?.RoleId ??
          0,
        ),

      menuId:
        Number(
          item?.menuId ??
          item?.MenuId ??
          0,
        ),

      menuName:
        String(
          item?.subRoleName ??
          item?.SubRoleName ??
          item?.menuName ??
          item?.MenuName ??
          "",
        ).trim(),

      menuCode:
        String(
          item?.subRoleCode ??
          item?.SubRoleCode ??
          item?.menuCode ??
          item?.MenuCode ??
          "",
        ).trim(),

      displayOrder:
        Number(
          item?.displayOrder ??
          item?.DisplayOrder ??
          0,
        ),

      parentMenuId:
        Number(
          item?.parentMenuId ??
          item?.ParentMenuId ??
          0,
        ),

      defaultChildMenuId:
        Number(
          item?.defaultChildMenuId ??
          item?.DefaultChildMenuId ??
          0,
        ),

      menuIconUrl:
        String(
          item?.menuIconUrl ??
          item?.MenuIconUrl ??
          "",
        ).trim(),

      templatePath:
        String(
          item?.templatePath ??
          item?.TemplatePath ??
          "",
        ).trim(),

      moduleName:
        String(
          item?.moduleName ??
          item?.ModuleName ??
          "",
        ).trim(),

      moduleId:
        Number(
          item?.moduleId ??
          item?.ModuleId ??
          0,
        ),

      userId:
        Number(
          item?.userId ??
          item?.UserId ??
          this.selectedUserId ??
          0,
        ),

      isDefault:
        Number(
          item?.isDefault ??
          item?.IsDefault ??
          0,
        ),

      isActive:
        Number(
          item?.isActive ??
          item?.IsActive ??
          1,
        ),

      isDeleted:
        Number(
          item?.isDeleted ??
          item?.IsDeleted ??
          0,
        ),

      isParent:
        Number(
          item?.isParent ??
          item?.IsParent ??
          0,
        ),

      childrenCount:
        Number(
          item?.childrenCount ??
          item?.ChildrenCount ??
          0,
        ),

      childIsParent:
        Number(
          item?.childIsParent ??
          item?.ChildIsParent ??
          0,
        ),

    };
  }


  // ============================================================
  // ACTIVE RECORD
  // ============================================================

  private isActiveRecord(
    item: any,
  ): boolean {

    const isActive =
      Number(
        item?.isActive ??
        item?.IsActive ??
        1,
      );


    const isDeleted =
      Number(
        item?.isDeleted ??
        item?.IsDeleted ??
        0,
      );


    return (
      isActive === 1
      &&
      isDeleted === 0
    );
  }


  // ============================================================
  // MODULE NORMALIZER
  // ============================================================

  private normalizeModuleName(
    value: any,
  ): string {

    const moduleName =
      String(
        value ||
        "",
      )
        .trim()
        .toLowerCase();


    if (
      moduleName === "school"
      ||
      moduleName === "school management"
      ||
      moduleName === "spe"
      ||
      moduleName === "scl"
    ) {

      return "school";
    }


    if (
      moduleName === "hospital"
      ||
      moduleName === "hm"
    ) {

      return "hospital";
    }


    if (
      moduleName === "society"
      ||
      moduleName === "housing society"
      ||
      moduleName === "sm"
    ) {

      return "society";
    }


    if (
      moduleName === "common"
      ||
      moduleName === "global"
    ) {

      return "common";
    }


    return moduleName;
  }


  // ============================================================
  // RESPONSE ARRAY
  // ============================================================

  private extractArray(
    response: any,
  ): any[] {

    if (
      !response
    ) {

      return [];
    }


    if (
      Array.isArray(
        response,
      )
    ) {

      return response;
    }


    const candidates = [

      response?.items,
      response?.Items,

      response?.data,
      response?.Data,

      response?.data?.items,
      response?.data?.Items,

      response?.Data?.items,
      response?.Data?.Items,

      response?.result,
      response?.Result,

      response?.result?.items,
      response?.result?.Items,

      response?.responseData,
      response?.ResponseData,

      response?.list,
      response?.List,

      response?.value,
      response?.Value,

    ];


    for (
      const candidate
      of candidates
    ) {

      if (
        Array.isArray(
          candidate,
        )
      ) {

        return candidate;
      }
    }


    return [];
  }


  // ============================================================
  // ACTION USER
  // ============================================================

  private getActionUser():
    string {

    return String(
      localStorage.getItem(
        "userId",
      )
      ||
      localStorage.getItem(
        "UserId",
      )
      ||
      "system",
    )
      .replace(
        /^"|"$/g,
        "",
      )
      .trim()
      ||
      "system";
  }


  // ============================================================
  // ERROR
  // ============================================================

  private showError(
    error: any,
    fallback: string,
  ): void {

    const message =
      error?.error?.message
      ??
      error?.error?.Message
      ??
      error?.error?.title
      ??
      error?.message
      ??
      fallback;


    this.toastr.error(
      String(
        message ||
        fallback,
      ),
    );
  }
}